<template>
    <div class="h-full">

        <Head :title="__(title)" />

        <board-view-menu :project="project" @filter-toggle="open_filter = !open_filter" @fClear="reset()"
            :filters="filters" view="test_cases" />
        <board-filter :project="project" @board-filter="open_filter = false" :filters="filters" v-if="open_filter"
            @do-filter="doFilter" options="user" />
        <div class="flex task__time_logs flex-col task__table h-[calc(100%-52px)] overflow-hidden overflow-y-auto">
            <div class="inline-block min-w-full py-4 align-middle md:px-3 lg:px-4">
                <div class="tiny__time__log__bar flex justify-between">
                    <search-input v-model="form.search" class="w-full max-w-md mr-4" @reset="reset" />
                    <div></div>
                    <div class="flex gap-2">
                        <button class="btn-indigo" @click="exportExcel">Export</button>
                        <button @click="openTestCaseCreatePopup" class="btn-indigo" v-if="role.slug === 'admin' || role.access.testcase.create">{{ __('Add Test Case') }}</button>
                    </div>
                </div>
                <div class="table__view">
                    <table>
                        <tr class=" text-gray-600 text-sm text-left">
                            <th class="px-6 pt-3 pb-2">{{ __('ID') }}</th>
                            <th class="px-6 pt-3 pb-2">{{ __('Test Case') }}</th>
                            <th class="px-6 pt-3 pb-2">{{ __('Status') }}</th>
                            <th class="px-6 pt-3 pb-2">{{ __('Estimated Time') }}</th>
                            <th class="px-6 pt-3 pb-2">{{ __('Created By') }}</th>
                            <th class="px-6 pt-3 pb-2">{{ __('Assigned To') }}</th>
                            <th class="px-6 pt-3 pb-2">{{ __('Date') }}</th>
                            <th class="px-6 pt-3 pb-2">{{ __('Action') }}</th>
                        </tr>
                        <tbody>
                            <tr v-for="testcase in test_cases.data" :key="testcase.id"
                                class="hover:bg-gray-100 focus-within:bg-gray-100 leading-5">
                                <td class="border-t">
                                    <div class="px-6 py-3 flex items-center gap-3 focus:text-indigo-500">    
                                        <span>TEST-{{ testcase.id }} </span>
                                    </div>
                                </td>
                                <td class="border-t">
                                    <div @click="openTestCaseEditPopup(testcase)"
                                        class="px-6 py-3 flex items-center focus:text-indigo-500 cursor-pointer font-medium">
                                        <h2>{{ testcase.name }}</h2>
                                    </div>
                                </td>
                                <td class="border-t w-px">
                                    <div class="px-6 py-3 flex items-center focus:text-indigo-500">
                                        {{ testcase.status == 1 ? 'Approved' : 'Draft' }}
                                    </div>
                                </td>
                                <td class="border-t">
                                    <div class="px-6 py-3 flex items-center focus:text-indigo-500">
                                        {{ testcase.estimated_time }}
                                    </div>
                                </td>
                                <td class="border-t">
                                    <div class="px-6 py-3 flex items-center focus:text-indigo-500">
                                        <div class="block rounded-full h-6 w-6">
                                            <img class="h-full w-full border border-white rounded-full"
                                                :src="testcase.owner.photo_path" :alt="testcase.owner.name">
                                        </div>
                                        <div class="ml-1">{{ testcase.owner.name }}</div>
                                    </div>
                                </td>
                                <td class="border-t">
                                    <div class="px-6 py-3 flex items-center focus:text-indigo-500" v-if="testcase.assignee">
                                        <div class="block rounded-full h-6 w-6">
                                            <img class="h-full w-full border border-white rounded-full"
                                                :src="testcase.assignee?.photo_path || '/images/user.svg'" :alt="testcase.assignee?.name">
                                        </div>
                                        <div class="ml-1">{{ testcase.assignee?.name }}</div>
                                    </div>
                                </td>
                                <td class="border-t">
                                    <div class="px-6 py-3 flex items-center focus:text-indigo-500">
                                        {{ moment(testcase.created_at).format('MMM D, YYYY [at] h:mm a') }}
                                    </div>
                                </td>
                                <td class="border-t">
                                    <div class="flex gap-2 px-6 py-3 flex items-center focus:text-indigo-500">
                                        <span 
                                            class="h-4 w-4 rounded-full"
                                            :class="{
                                                'bg-green-500': testcase.execution_status === 'PASS',
                                                'bg-red-500': testcase.execution_status === 'FAIL',
                                                'bg-orange-500': testcase.execution_status === 'WIP',
                                                'bg-gray-500': !['PASS', 'FAIL', 'WIP'].includes(testcase.execution_status),
                                            }"
                                            :title="testcase.execution_status"
                                            ></span>
                                        <button @click="viewTestScript(testcase)"
                                            class="bg-indigo-600 text-white p-[5px] rounded disabled:opacity-50 text-sm">Test
                                            Steps</button>
                                        <button @click="executeTestScript(testcase)"
                                            class="bg-green-600 text-white p-[5px] rounded disabled:opacity-50 text-sm">
                                            <div class="flex items-center">
                                                <icon name="play" class="w-4 fill-[#ffffff] h-4 mr-[5px]" />
                                                <span>{{ __('Execute') }}</span>
                                            </div>
                                        </button>
                                        <button @click="deleteTestCase(testcase)"
                                            class="bg-red-600 text-white p-[5px] rounded disabled:opacity-50 text-sm"
                                            v-if="role.slug === 'admin' || role.access.testcase.delete">
                                            <div class="flex items-center">
                                                <icon name="trash" class="w-4 fill-[#ffffff] h-4 text-xs" />
                                            </div>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="test_cases.data.length === 0">
                                <td class="border-t px-6 py-4" colspan="6">{{ __('No time log found.') }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="flex w-full px-3 pb-3">
                        <pagination class="mt-1" :links="test_cases.links" />
                    </div>
                </div>
            </div>
        </div>
        <add-test-case v-if="openCreateTestCase" :id="id" :project="project" :members = "members" :isUpdate="isUpdate" @closeModal="closeDetails()" />
        <test-script v-if="openTestScript" :id="id" :project="project" @closeModal="closeDetails()" />
        <execute-test-script v-if="openExecuteScript" :test_case="selected_test_case" :project="project" @closeModal="closeDetails()" @getTestCase="getTestCase"/>
        <delete-confirmation v-if="delete_testcase" @popup="delete_testcase = false" @confirm="destroy()" details="Are you sure you want to delete this test case?"/>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Pagination from '@/Shared/Pagination'
import mapValues from 'lodash/mapValues'
import throttle from 'lodash/throttle'
import BoardViewMenu from '@/Shared/BoardViewMenu'
import moment from 'moment'
import axios from 'axios'
import SearchInput from '@/Shared/SearchInput'
import BoardFilter from "../../Shared/BoardFilter";
import AddTestCase from "@/Pages/TestCase/Modals/AddTestCase";
import TestScript from "@/Pages/TestCase/Modals/TestScript";
import ExecuteTestScript from "@/Pages/TestCase/Modals/ExecuteTestScript";
import DeleteConfirmation from "@/Shared/DeleteConfirmation";


export default {
    components: {
        BoardFilter,
        Head,
        Icon,
        Link,
        BoardViewMenu,
        Pagination,
        SearchInput,
        TestScript,
        AddTestCase,
        ExecuteTestScript,
        DeleteConfirmation
    },
    layout: Layout,
    props: {
        title: String,
        auth: Object,
        project: Object,
        workspace: Object,
        test_cases: Object,
        filters: Object,
        test_case: Object,
        members: Object
    },
    data() {
        return {
            id: null,
            open_filter: false,
            selected_test_case: {},
            test_case_script: {},
            delete_testcase: false,
            form: {
                search: this.filters.search,
                user: this.filters.user,
                due: this.filters.due,
                label: this.filters.label
            },
            loading: false,
            isUpdate: false,
            openCreateTestCase: false,
            openTestScript: false,
            openExecuteScript: false,
            role: this.$page.props.auth.user.role
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.route('projects.view.test_cases', this.project.slug || this.project.id), pickBy(this.form), { preserveState: true })
            }, 150),
        },
        test_case_script: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.route('projects.view.test_cases', this.project.slug || this.project.id), pickBy(this.form), { preserveState: true })
            }, 150),
        }
    },
    computed: {

    },
    created() {
        this.moment = moment
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const mode = urlParams.get('mode');
        if(this.test_case && mode === 'script') {
            this.viewTestScript(this.test_case);
        } else if(this.test_case && mode === 'execute') {
            this.executeTestScript(this.test_case);
        } else if(this.test_case) {
            this.openTestCaseEditPopup(this.test_case);
        }
    },
    methods: {
        doFilter(form) {
            Object.assign(this.form, form);
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        closeDetails() {
            this.openCreateTestCase = false
            this.openTestScript = false;
            this.openExecuteScript = false;
        },
        openTestCaseCreatePopup() {
            this.isUpdate = false;
            this.openCreateTestCase = true;
        },
        openTestCaseEditPopup(testcase) {
            if(this.role.slug === 'admin' || this.role.access.testcase.edit) {
                this.isUpdate = true;
                this.id = testcase.id;
                this.openCreateTestCase = true;
            }
        },
        viewTestScript(testcase) {
            this.id = testcase.id;
            this.openTestScript = true;
        },
        executeTestScript(testcase) {
            this.id = testcase.id;
            this.selected_test_case = testcase;
            this.openExecuteScript = true;
        },
        async getTestCase(id=null) {
            const testCaseResponse = await axios.get(this.route('json.test_case.get', id == null ? this.selected_test_case.id : id));
            this.selected_test_case = testCaseResponse.data;
            this.test_case_script = testCaseResponse.data;
        },
        deleteTestCase(test_case) {
            this.delete_testcase = true;
            this.selected_test_case = test_case;
        } ,
        destroy() {
            this.delete_testcase = false;
            axios.delete(this.route('test_case.delete', this.selected_test_case.id)).then((response) => {
                this.test_case_script = 'Reload';
            })
        },
        exportExcel() {
            if(confirm('Are you sure you want to export this test case?')) {
                window.open(this.route('export.test_case', this.project.slug || this.project.id), '_blank');
            }
        }
    },
}
</script>
